import ReactSelect, { Props as SelectProps } from 'react-select';

export type MultiSelectOption = {
  value: string | number;
  label: string;
};

interface MultiSelectProps
  extends Omit<SelectProps<MultiSelectOption, true>, 'value' | 'onChange'> {
  label?: string;
  value?: MultiSelectOption[];
  onChange?: (value: MultiSelectOption[]) => void;
  error?: string;
  required?: boolean;
}

export function MultiSelect({
  label,
  value,
  onChange,
  error,
  required,
  placeholder = 'Select options...',
  ...props
}: MultiSelectProps) {
  return (
    <div className="space-y-2">
      {label && (
        <label className="block text-sm font-medium text-foreground">
          {label}
          {required && <span className="text-danger ml-1">*</span>}
        </label>
      )}
      <ReactSelect
        {...props}
        value={value}
        onChange={(newValue) => onChange?.(newValue as MultiSelectOption[])}
        isMulti
        placeholder={placeholder}
        className="w-full"
        classNamePrefix="nextui-select"
        styles={{
          control: (base, state) => ({
            ...base,
            minHeight: '40px',
            backgroundColor: 'white',
            borderColor: error
              ? '#dc2626'
              : state.isFocused
                ? '#2563eb'
                : '#e5e7eb',
            borderWidth: state.isFocused ? '2px' : '1px',
            borderRadius: '8px',
            boxShadow: 'none',
            '&:hover': {
              borderColor: error ? '#dc2626' : '#2563eb',
            },
            transition: 'border-color 0.15s ease',
          }),
          menu: (base) => ({
            ...base,
            backgroundColor: 'white',
            border: '1px solid #e5e7eb',
            borderRadius: '8px',
            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
            overflow: 'hidden',
            zIndex: 10000,
          }),
          menuList: (base) => ({
            ...base,
            padding: '6px',
            backgroundColor: 'white',
            '&::-webkit-scrollbar': {
              width: '6px',
            },
            '&::-webkit-scrollbar-track': {
              background: '#f1f1f1',
            },
            '&::-webkit-scrollbar-thumb': {
              background: '#d1d5db',
              borderRadius: '3px',
            },
          }),
          option: (base, state) => ({
            ...base,
            padding: '8px 12px',
            backgroundColor: state.isSelected
              ? '#2563eb'
              : state.isFocused
                ? '#f3f4f6'
                : 'white',
            color: state.isSelected ? 'white' : '#374151',
            cursor: 'pointer',
            borderRadius: '6px',
            margin: '2px 0',
            '&:hover': {
              backgroundColor: state.isSelected ? '#2563eb' : '#f3f4f6',
            },
            '&:active': {
              backgroundColor: state.isSelected ? '#1d4ed8' : '#e5e7eb',
            },
          }),
          multiValue: (base) => ({
            ...base,
            backgroundColor: '#e5e7eb',
            borderRadius: '6px',
            margin: '2px 4px 2px 0',
          }),
          multiValueLabel: (base) => ({
            ...base,
            color: '#374151',
            fontSize: '0.875rem',
            padding: '2px 6px',
          }),
          multiValueRemove: (base) => ({
            ...base,
            color: '#374151',
            cursor: 'pointer',
            borderRadius: '0 6px 6px 0',
            padding: '0 4px',
            '&:hover': {
              backgroundColor: '#fee2e2',
              color: '#dc2626',
            },
          }),
          input: (base) => ({
            ...base,
            color: '#374151',
            margin: '2px',
          }),
          placeholder: (base) => ({
            ...base,
            color: '#9ca3af',
          }),
          singleValue: (base) => ({
            ...base,
            color: '#374151',
          }),
          valueContainer: (base) => ({
            ...base,
            padding: '0 12px',
          }),
          clearIndicator: (base) => ({
            ...base,
            color: '#9ca3af',
            padding: '4px',
            '&:hover': {
              color: '#374151',
            },
          }),
          dropdownIndicator: (base) => ({
            ...base,
            color: '#9ca3af',
            padding: '4px',
            '&:hover': {
              color: '#374151',
            },
          }),
          container: (base) => ({
            ...base,
            backgroundColor: 'var(--nextui-input-bg)',
          }),
        }}
      />
      {error && <p className="text-tiny text-danger">{error}</p>}
    </div>
  );
}
